import { Component, Input, OnInit, OnChanges } from '@angular/core';
import type { SimpleChanges } from '@angular/core';
import { Measure, PerformanceArea, TierData } from 'src/app/shared/models/performance-measures.model';

@Component({
  selector: 'app-odp-performance-measures',
  templateUrl: './odp-performance-measures.component.html',
  styleUrls: ['./odp-performance-measures.component.scss'],
  standalone: false
})
export class OdpPerformanceMeasuresComponent implements OnInit, OnChanges {
  @Input() performanceAreas: PerformanceArea[] = [];
  @Input() tierMeasureRequirements: TierData[] = [];

  requirementsData: any[] = [];
  visibleTiers: { code: string; label: string; field: string }[] = [];
  dataSource: any[] = [{ data: 'performance-measures' }];

  constructor() {}

  ngOnInit(): void {
    console.log('Tier Measure Requirements:', this.tierMeasureRequirements);

    if (this.tierMeasureRequirements && this.tierMeasureRequirements.length > 0) {
      this.prepareTableData();
    }

    this.expandAll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tierMeasureRequirements'] && !changes['tierMeasureRequirements'].firstChange) {
      console.log('Tier Measure Requirements updated:', this.tierMeasureRequirements);

      if (this.tierMeasureRequirements && this.tierMeasureRequirements.length > 0) {
        this.prepareTableData();
      }
    }

    if (changes['performanceAreas'] && !changes['performanceAreas'].firstChange) {
      this.expandAll();
    }
  }

  prepareTableData(): void {
    const grouped = new Map<string, any>();

    const sortedTiers = [...this.tierMeasureRequirements]
      .filter(t => t.isTierVisible)
      .sort((a, b) => (a.tierSequence || 0) - (b.tierSequence || 0));

    sortedTiers.forEach(tier => {
      const field = tier.tierCode.toLowerCase();

      (tier.measures || []).forEach(m => {
        if (!grouped.has(m.measureCode)) {
          grouped.set(m.measureCode, {
            measureCode: m.measureCode,
            measureDescription: m.measureDescription,
            _seq: m.measureSequence || 0
          });
        }

        const row = grouped.get(m.measureCode);
        row[field] = m.isRequired ? '✓' : '--';
      });
    });

    const allFields = sortedTiers.map(t => t.tierCode.toLowerCase());

    grouped.forEach(row => {
      allFields.forEach(f => {
        if (row[f] === undefined) {
          row[f] = '--';
        }
      });
    });

    this.requirementsData = Array.from(grouped.values())
      .sort((a, b) => a._seq - b._seq)
      .map(({ _seq, ...rest }) => rest);

    this.visibleTiers = sortedTiers.map(tier => ({
      code: tier.tierCode,
      label: tier.tierName,
      field: tier.tierCode.toLowerCase()
    }));
  }

  expandAll(): void {
    if (!this.performanceAreas || this.performanceAreas.length === 0) {
      return;
    }

    this.performanceAreas.forEach(area => {
      area.expanded = true;

      if (area.measures && area.measures.length > 0) {
        area.measures.forEach(measure => {
          measure.expanded = true;
        });
      }
    });
  }

  collapseAll(): void {
    if (!this.performanceAreas || this.performanceAreas.length === 0) {
      return;
    }

    this.performanceAreas.forEach(area => {
      area.expanded = false;

      if (area.measures && area.measures.length > 0) {
        area.measures.forEach(measure => {
          measure.expanded = false;
        });
      }
    });
  }

  toggleArea(areaIndex: number): void {
    if (!this.performanceAreas || !this.performanceAreas[areaIndex]) {
      return;
    }

    this.performanceAreas[areaIndex].expanded = !this.performanceAreas[areaIndex].expanded;
  }

  toggleMeasure(areaIndex: number, measureIndex: number): void {
    if (
      !this.performanceAreas ||
      !this.performanceAreas[areaIndex] ||
      !this.performanceAreas[areaIndex].measures ||
      !this.performanceAreas[areaIndex].measures[measureIndex]
    ) {
      return;
    }

    this.performanceAreas[areaIndex].measures[measureIndex].expanded =
      !this.performanceAreas[areaIndex].measures[measureIndex].expanded;
  }

  getTotalMeasures(area: PerformanceArea): number {
    return area?.measures?.length || 0;
  }

  trackMeasure(index: number, measure: Measure): string {
    return index.toString();
  }

  getTierBadgeClass(tier: string): string {
    if (!tier) {
      return '';
    }

    const t = tier.toLowerCase().replace(/[\s_-]/g, '');

    if (t === 'primary') {
      return 'badge-primary-level';
    }

    if (t === 'select') {
      return 'badge-select-level';
    }

    if (t === 'ce' || t === 'clinicallyenhanced') {
      return 'badge-clinically-enhanced';
    }

    if (t === 'recommended') {
      return 'badge-recommended';
    }

    return 'badge-default';
  }

  getRecommendedTier(): string {
    if (!this.performanceAreas || this.performanceAreas.length === 0) {
      return '';
    }

    let highestTierLevel = 0;
    let highestTier = '';

    this.performanceAreas.forEach(area => {
      if (!area.measures || area.measures.length === 0) {
        return;
      }

      area.measures.forEach(measure => {
        if (measure.recommendedTier && measure.recommendedTierLevel) {
          if (measure.recommendedTierLevel > highestTierLevel) {
            highestTierLevel = measure.recommendedTierLevel;
            highestTier = measure.recommendedTier;
          }
        }
      });
    });

    if (!highestTier) {
      return '';
    }

    const words = highestTier.trim().split(/\s+/);

    if (words.length === 2) {
      return words[0][0].toUpperCase() + words[1][0].toUpperCase();
    }

    return highestTier;
  }

  getRecommendedTierAbbreviation(tier: string): string {
    if (!tier) {
      return '';
    }

    const words = tier.trim().split(/\s+/);

    if (words.length === 2) {
      return words[0][0].toUpperCase() + words[1][0].toUpperCase();
    }

    return tier;
  }
}